/* ==========================================================================
   zoom-reveal-stage.js
   --------------------------------------------------------------------------
   Owned by sections/zoom-reveal-stage.liquid. Loaded deferred by that section.

   WHAT THIS FILE IS RESPONSIBLE FOR, AND WHAT IT IS NOT

   It is NOT responsible for the stage looking right, and it is NOT responsible
   for the list being readable. The finished stage is the stylesheet's base
   state and the no-script list is a plain headed price list with every panel
   open, so with this file blocked, 404ing or simply slow the visitor still gets
   a usable, readable, fully composed surface. Everything here is additive.

   It does three things:

     1. DRIVES THE PUSH. Wait out the hold, attach the transition, drop the
        start transform, and — the part that matters — REMOVE every property
        involved when the push ends. Not reset: remove. `transform: none` still
        makes an element the containing block for its position:fixed
        descendants, so a dialog opened from the surface would be positioned
        against the zoom wrapper instead of the viewport. An absent property
        does not do that. Same for will-change. This is the single reason the
        finish path is written the way it is.

     2. MAKES THE INTRO SKIPPABLE BY ANYTHING. Click, tap, key, wheel, scroll,
        or the skip button. An intro that has to be sat through is an intro
        somebody will bounce off.

     3. DRIVES THE DISCLOSURE LIST the section renders on the surface — the
        sequenced close-then-open, the guard, and the completion signal for a
        height animation it does not own. See THE LIST below.

     Plus one piece of bookkeeping: it RECORDS THE PLAY in sessionStorage, so a
     repeat view in the same session renders finished. The read happens in the
     section's inline primer, before first paint, because that decision has to
     be made before the browser has painted anything.

   THREE THINGS IT DELIBERATELY DOES NOT DO

     · It does not lock scroll, trap focus, or open anything fixed. The list is
       part of the page; KitInteractions.lockScroll is never called.
     · It does not decide whether to play the intro. The primer does,
       synchronously, and signals its decision by having already added the armed
       class. This file only ever asks "am I armed?".
     · It does not move markup between sections. An earlier revision adopted an
       element from elsewhere in the document onto the surface at runtime. That
       whole mechanism is gone: the section renders its own list, so there is
       nothing to relocate and nothing to put back.

   THE LIST — the four things that are load-bearing

     · NO KEY HANDLER. The toggle is a real `button`, so Enter and Space both
       activate it and both fire `click`. A keydown handler here would be a
       second, competing implementation of something the platform already does
       correctly — and the usual way Space ends up scrolling the page as well as
       opening a category.
     · SEQUENCED EXCLUSIVITY. Opening a closed category closes whichever is open
       FIRST and expands the new one only once that collapse has actually
       finished. Two heights moving in opposite directions in the same frame is
       what makes a simultaneous accordion feel like a glitch: the page grows and
       shrinks at once and the row you clicked walks away from the pointer.
     · THE `animating` GUARD drops any click that lands mid-sequence, plus a
       per-panel `zrsBusy` flag so that a multi-open list still ignores a
       double-click on ONE row without freezing the other rows.
     · THE LIST IS LOCKED SHUT FOR THE LENGTH OF THE INTRO, and that is geometry
       rather than tidiness. The push is scaled and centred around the surface's
       height; an open category changes that height; a height that depends on
       merchant state or on a stale session is the same class of bug as a
       backdrop that re-crops itself at every window shape. So a stage that is
       going to play an intro collapses every open panel and refuses to open one
       until the push has finished — see Menu's `locked`. The panels the merchant
       marked open are collapsed by the section's inline primer BEFORE the first
       paint, so nothing is ever seen snapping shut.
     · THE HEIGHT ANIMATION IS CSS, NOT JS. The stylesheet transitions
       `grid-template-rows: 0fr -> 1fr`, so nothing here measures or writes a
       pixel height and the easing is honest at any content length — including
       while the whole surface is mid-scale during the intro, when a measured
       pixel height would be measured through a transform. What this file needs
       is only a COMPLETION SIGNAL, which is `transitionend` filtered to that
       property, with a timer as the safety net: an engine that does not
       interpolate the fr value snaps instead of animating and never fires the
       event, and reduced motion removes the transition entirely. Either way the
       sequence must still advance, or the guard never clears and the list is
       dead.

   THEME EDITOR. window.Shopify.designMode is handled in the primer, which never
   arms — so a merchant editing the surface never fights the animation. This
   file still runs in the editor, for the list, and re-runs on
   shopify:section:load. Selecting a category or a row in the sidebar opens the
   category it belongs to, because a closed panel means a merchant clicking a
   row sees nothing happen and reasonably concludes the setting is broken.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-zoom-reveal-stage]';
  var CLASS_ARMED = 'zoom-reveal-stage--armed';
  var CLASS_MOTION = 'zoom-reveal-stage--motion';

  /* An array rather than a WeakMap: this collection has to be SWEPT (walked to
     find instances whose root has been detached by the theme editor), and a
     WeakMap cannot be walked. Entries are removed in the same sweep, so it does
     not grow. */
  var instances = [];

  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function readInt(el, name, fallback) {
    var value = parseInt(el.getAttribute(name), 10);
    return isNaN(value) ? fallback : value;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* sessionStorage throws rather than returning null in a locked-down privacy
     mode, and it throws on ACCESS, not on use. Every call site here treats a
     throw as "no history", which fails towards playing the intro rather than
     towards a broken page. */
  function rememberPlayed(key) {
    if (!key) return;
    try {
      window.sessionStorage.setItem(key, '1');
    } catch (e) {
      /* no session history available; the intro simply plays again */
    }
  }

  function revealContent(el) {
    try {
      if (window.KitInteractions && typeof window.KitInteractions.revealNow === 'function') {
        window.KitInteractions.revealNow(el);
      }
    } catch (e) {
      /* the reveal observer is an enhancement; never let it break the finish */
    }
  }

  /* ======================================================================
     THE LIST
     ====================================================================== */

  /* `locked` is passed in by the stage and means "an intro is about to play".
     A category open during the intro makes the surface taller, and the surface's
     height is what the push is scaled and centred around — so an open panel
     moves the paper off the prop, which is the exact unpredictability this
     revision exists to remove. Locked therefore means three things: collapse
     anything the merchant set to start open, ignore every toggle, and hold the
     address-bar deep link until the push has finished rather than dropping it. */
  function Menu(root, locked) {
    this.root = root;
    this.locked = !!locked;
    this.toggles = slice(root.querySelectorAll('[data-zrs-toggle]'));
    this.panels = slice(root.querySelectorAll('[data-zrs-panel-el]'));

    /* Default ON, matching the schema: the attribute has to be explicitly
       "false" to turn exclusivity off, so a missing attribute (an older saved
       section, a merchant who has never opened the setting) behaves like the
       reference rather than like a multi-open list. */
    this.singleOpen = root.getAttribute('data-zrs-menu-single') !== 'false';

    this.animating = false;
    this.waiters = [];
    this.unbinders = [];

    if (this.toggles.length === 0 || this.panels.length === 0) return;

    var self = this;
    this.onToggleClick = function (event) {
      self.handleToggle(event.currentTarget);
    };

    this.toggles.forEach(function (toggle) {
      toggle.addEventListener('click', self.onToggleClick);
      self.unbinders.push(function () {
        toggle.removeEventListener('click', self.onToggleClick);
      });
    });

    this.bindEditor();

    if (this.locked) {
      this.closeAll();
    } else {
      this.openFromHash();
    }
  }

  /* The section's inline primer has already collapsed every open panel before
     the first paint, so this normally finds nothing to do. It runs anyway, and
     it is not belt and braces: the primer only runs on a first load, while this
     constructor also runs on a theme-editor re-render and on a section reload,
     where freshly rendered markup can arrive open again. Instant, because there
     is nothing to animate FROM — the panel has never been seen open. */
  Menu.prototype.closeAll = function () {
    var self = this;
    this.panels.forEach(function (panel) {
      if (!self.isOpen(panel)) return;
      self.markInstant(panel);
      self.setOpen(panel, false);
    });
  };

  /* Called by the stage when the push has finished OR been skipped — both end
     the intro, and both mean the geometry no longer depends on the list. The
     deep link is honoured here rather than at construction so that arriving at
     #colour still opens colour, one moment later. */
  Menu.prototype.unlock = function () {
    if (!this.locked) return;
    this.locked = false;
    this.openFromHash();
  };

  Menu.prototype.panelFor = function (toggle) {
    var id = toggle.getAttribute('data-zrs-panel');
    if (!id) return null;
    for (var i = 0; i < this.panels.length; i++) {
      if (this.panels[i].id === id) return this.panels[i];
    }
    return null;
  };

  Menu.prototype.toggleFor = function (panel) {
    for (var i = 0; i < this.toggles.length; i++) {
      if (this.toggles[i].getAttribute('data-zrs-panel') === panel.id) return this.toggles[i];
    }
    return null;
  };

  Menu.prototype.isOpen = function (panel) {
    return panel.hasAttribute('data-zrs-open');
  };

  /* ONE state change, in one place. The attribute drives the height and the
     visibility; aria-expanded drives the indicator and the announcement. Writing
     them together is what stops the two from ever disagreeing. */
  Menu.prototype.setOpen = function (panel, open) {
    if (open) {
      panel.setAttribute('data-zrs-open', '');
    } else {
      panel.removeAttribute('data-zrs-open');
    }

    var toggle = this.toggleFor(panel);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    /* Alpha's reveal observer marks anything that has not intersected the
       viewport as offscreen, and a collapsed panel is a zero-height box that
       never intersects. Without this call a category can open empty. */
    if (open) revealContent(panel);
  };

  /* A panel opened on load — from the address bar, or by the theme editor —
     must not unroll from nothing; that reads as a glitch rather than as state.
     Two frames, because one is not enough to guarantee the suppressed style has
     been computed before it is withdrawn. */
  Menu.prototype.markInstant = function (panel) {
    panel.setAttribute('data-zrs-instant', '');
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        panel.removeAttribute('data-zrs-instant');
      });
    });
  };

  Menu.prototype.handleToggle = function (toggle) {
    /* While the intro is running the zoom wrapper has pointer-events: none, so a
       pointer click cannot reach a toggle at all. A keyboard activation can —
       the toggles are still real buttons in the tab order — and it must not
       change the surface's height mid-push. It is not swallowed silently
       either: any key is also a skip, so the same keystroke ends the intro and
       the visitor's next press works normally. */
    if (this.locked) return;

    var panel = this.panelFor(toggle);
    if (!panel) return;

    /* THE GUARD, in two parts. `animating` is the sequence lock and blocks every
       row while a close-then-open is in flight. `zrsBusy` is per panel, so with
       exclusivity switched off a second click on the row you are already opening
       is dropped while the other rows stay live — a global lock there would make
       a multi-open list feel broken. */
    if (this.animating || panel.zrsBusy) return;

    if (this.isOpen(panel)) {
      this.collapse(panel);
    } else {
      this.open(panel);
    }
  };

  /* THE SEQUENCE. Close what is open, wait for it to finish, then expand.
     A list rather than a single reference because multi-open mode can leave more
     than one panel open and then be switched back to single-open by a merchant
     mid-session; closing "the open one" would leave the others up. */
  Menu.prototype.open = function (panel, instant) {
    /* The second half of the lock, on the method every other entry point funnels
       through — the hash link and the editor's block:select as well as a click.
       One test here is what makes "nothing opens during the intro" a property of
       the object rather than of the four callers remembering. */
    if (this.locked) return;
    if (this.isOpen(panel)) return;

    var self = this;
    var closing = [];

    if (this.singleOpen) {
      this.panels.forEach(function (other) {
        if (other !== panel && self.isOpen(other)) closing.push(other);
      });
    }

    if (instant) {
      this.markInstant(panel);
      closing.forEach(function (other) {
        self.markInstant(other);
      });
    }

    if (closing.length === 0) {
      this.expand(panel);
      return;
    }

    /* The guard covers the WHOLE sequence — the collapse and the expansion after
       it — not just the collapse. A click accepted between the two halves would
       start a second sequence against a panel that is already mid-open, and the
       two would race to set aria-expanded. */
    this.animating = true;

    var pending = closing.length;

    closing.forEach(function (other) {
      other.zrsBusy = true;
      self.afterMotion(other, function () {
        other.zrsBusy = false;
        pending -= 1;
        if (pending > 0) return;
        self.expand(panel, function () {
          self.animating = false;
        });
      });
      self.setOpen(other, false);
    });
  };

  Menu.prototype.expand = function (panel, done) {
    panel.zrsBusy = true;
    this.setOpen(panel, true);
    this.afterMotion(panel, function () {
      panel.zrsBusy = false;
      if (done) done();
    });
  };

  Menu.prototype.collapse = function (panel) {
    if (!this.isOpen(panel)) return;
    panel.zrsBusy = true;
    this.afterMotion(panel, function () {
      panel.zrsBusy = false;
    });
    this.setOpen(panel, false);
  };

  /* WAITING FOR A HEIGHT TO FINISH, without owning the height.

     The stylesheet animates grid-template-rows 0fr -> 1fr, so this only needs
     the moment it lands. transitionend is the signal; the timer is the safety
     net, and it is not optional:

       · reduced motion and [data-zrs-instant] remove the transition entirely, so
         the event never fires at all
       · an engine that does not interpolate an fr value snaps to the end state
         and, again, fires nothing
       · a panel torn down mid-transition (theme editor re-render) fires nothing
         either

     In every one of those cases the sequence still has to advance or the guard
     never clears and the list locks. The duration is READ FROM THE COMPUTED
     STYLE rather than written here, so the P31 motion tokens stay the single
     source of timing and this file holds no duration literal. */
  Menu.prototype.afterMotion = function (panel, done) {
    var self = this;
    var duration = this.motionDuration(panel);
    var settled = false;
    var timer = null;

    function cleanup() {
      panel.removeEventListener('transitionend', onEnd);
      if (timer) window.clearTimeout(timer);
      var index = self.waiters.indexOf(cleanup);
      if (index !== -1) self.waiters.splice(index, 1);
    }

    function finish() {
      if (settled) return;
      settled = true;
      cleanup();
      done();
    }

    /* transitionend bubbles, so a transition on anything inside the panel — a
       hover on a row, a button — would otherwise be read as "the panel has
       finished opening". Both tests are load-bearing. */
    function onEnd(event) {
      if (event.target !== panel) return;
      if (event.propertyName !== 'grid-template-rows') return;
      finish();
    }

    this.waiters.push(cleanup);

    /* Still asynchronous when there is no animation: `done` can open the next
       panel, and doing that from inside the caller's own forEach makes the order
       of two state changes depend on the order of a loop. */
    if (duration <= 0) {
      timer = window.setTimeout(finish, 0);
      return;
    }

    panel.addEventListener('transitionend', onEnd);
    timer = window.setTimeout(finish, duration + 80);
  };

  Menu.prototype.motionDuration = function (panel) {
    if (typeof window.getComputedStyle !== 'function') return 0;

    var raw = window.getComputedStyle(panel).transitionDuration || '';
    var first = raw.split(',')[0].trim();
    if (!first) return 0;

    var value = parseFloat(first);
    if (!isFinite(value) || value <= 0) return 0;

    /* Computed style normalises to seconds in every current engine, but the unit
       is checked rather than assumed: reading 400ms as 400 seconds would wedge
       the guard for six and a half minutes. */
    return first.indexOf('ms') !== -1 ? value : value * 1000;
  };

  /* Cancel, never finish. A pending `done` opens panels, and running it during
     teardown would fight whatever is replacing this section. */
  Menu.prototype.cancelWaiters = function () {
    slice(this.waiters).forEach(function (cancel) {
      cancel();
    });
    this.waiters = [];
    this.animating = false;
  };

  /* The `handle` setting, made real. A hash matching a panel's id or its key
     opens that category on arrival. Read only — nothing here writes to the
     address bar, because a disclosure inside a page is a view of that page and
     a history entry per category would make Back mean "close the thing I opened
     four categories ago". */
  Menu.prototype.openFromHash = function () {
    if (isDesignMode()) return;

    var raw = '';
    try {
      raw = (window.location.hash || '').replace(/^#/, '');
    } catch (e) {
      return;
    }
    if (!raw) return;

    var key = raw.toLowerCase();
    var match = null;

    for (var i = 0; i < this.panels.length; i++) {
      var panel = this.panels[i];
      if (panel.id === raw) {
        match = panel;
        break;
      }
      var candidate = (panel.getAttribute('data-zrs-key') || '').toLowerCase();
      if (candidate && candidate === key && !match) match = panel;
    }

    if (match) this.open(match, true);
  };

  /* Mandatory, not a nicety: the panels are closed by default, so a merchant who
     clicks a row in the sidebar sees nothing happen and reasonably concludes the
     setting is broken. Same reasoning as the tab controller's
     shopify:block:select handling in assets/kit-interactions.js. */
  Menu.prototype.bindEditor = function () {
    if (!isDesignMode()) return;

    var self = this;
    var onSelect = function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!self.root.contains(target)) return;

      /* Two shapes of selected block. A `category` block's editor attributes
         live on its ITEM WRAPPER, which CONTAINS a panel rather than sitting
         inside one, so closest() cannot find the panel from it — hence the
         second lookup. A `price_row` block's attributes live on the row inside
         the panel, where closest() does find it. */
      var panel = target.closest('[data-zrs-panel-el]');

      if (!panel) {
        var item = target.closest('[data-zrs-item]');
        var toggle = item ? item.querySelector('[data-zrs-toggle]') : null;
        if (toggle) panel = self.panelFor(toggle);
      }

      if (panel && !self.isOpen(panel)) self.open(panel, true);
    };

    document.addEventListener('shopify:block:select', onSelect);
    this.unbinders.push(function () {
      document.removeEventListener('shopify:block:select', onSelect);
    });
  };

  Menu.prototype.destroy = function () {
    this.cancelWaiters();
    for (var i = 0; i < this.unbinders.length; i++) this.unbinders[i]();
    this.unbinders = [];
  };

  /* ======================================================================
     THE STAGE
     ====================================================================== */

  function Stage(root) {
    this.root = root;
    this.zoom = root.querySelector('[data-zrs-zoom]');
    this.surface = root.querySelector('[data-zrs-surface]');
    this.skipButton = root.querySelector('[data-zrs-skip]');
    this.sessionKey = root.getAttribute('data-zrs-session-key') || '';
    this.holdMs = readInt(root, 'data-zrs-hold', 2000);
    this.pushMs = readInt(root, 'data-zrs-push', 1800);

    this.finished = false;
    this.timers = [];
    this.unbinders = [];

    root.setAttribute('data-zrs-ready', 'true');

    /* Armed is read BEFORE the list is built, because it is the list's
       constructor argument. Not armed means the primer decided this view is not
       an intro — repeat visit, reduced motion, design mode, or the setting is
       off — and on those views open_by_default is honoured untouched. */
    var armed = root.classList.contains(CLASS_ARMED);

    /* The list is built unconditionally. It is the content; the intro is
       decoration on top of it, and a stage that is not playing an intro still
       has a list to drive. */
    this.menu = new Menu(root, armed);

    if (!armed) {
      this.finished = true;
      return;
    }

    if (!this.zoom) {
      this.finish();
      return;
    }

    /* An intro nobody can see is a 2-second delay in front of content somebody
       has already scrolled past — deep link, back-forward restore, or a stage
       that is simply not the first section. Cheaper than an
       IntersectionObserver and answers the same question. */
    var box = root.getBoundingClientRect();
    if (box.bottom <= 0 || box.top >= (window.innerHeight || 0)) {
      this.finish();
      return;
    }

    rememberPlayed(this.sessionKey);
    this.trackStageWidth();
    this.bindSkips();
    this.timers.push(window.setTimeout(this.push.bind(this), this.holdMs));
  }

  /* ------------------------------------------------------------ measurement */

  /* THE ONE NUMBER CSS CANNOT WORK OUT FOR ITSELF. The start transform positions
     the surface as a fraction of the backdrop box, and that box is
     min(stage width, stage height x ratio) — a length, so its width term cannot
     be a percentage of an ancestor. The stylesheet's fallback is 100vw, which is
     right to within the width of a scrollbar; this replaces it with the stage's
     measured width, which is right exactly.

     It is written before the hold and removed at finish, because after the push
     no transform reads it. Nothing depends on this running: with the script
     blocked the fallback stands, and the error it leaves is a fraction of a
     scrollbar's width. */
  Stage.prototype.trackStageWidth = function () {
    var self = this;
    var apply = function () {
      var width = self.root.clientWidth;
      if (width > 0) self.root.style.setProperty('--zrs-stage-width', width + 'px');
    };

    apply();
    window.addEventListener('resize', apply, { passive: true });
    this.unbinders.push(function () {
      window.removeEventListener('resize', apply, { passive: true });
      self.root.style.removeProperty('--zrs-stage-width');
    });
  };

  /* -------------------------------------------------------------------- skip */

  Stage.prototype.bindSkips = function () {
    var self = this;
    var finish = function () {
      self.finish();
    };

    if (this.skipButton) {
      this.skipButton.addEventListener('click', finish);
      this.unbinders.push(function () {
        self.skipButton.removeEventListener('click', finish);
      });
    }

    /* Listeners go on the document in the CAPTURE phase so a click on a link
       inside the surface skips before the navigation is considered, and so
       nothing inside the surface can stop the skip by stopping propagation.
       They are all passive: none of them calls preventDefault, and a
       non-passive wheel or touch listener on the document is a scroll-jank
       report waiting to happen.

       Note the interaction with the list: while the intro is running the zoom
       wrapper has pointer-events:none, so a click aimed at a category header
       never reaches the header at all — it lands here and skips. Once the push
       has finished these listeners are gone and the header takes its own
       clicks. There is no window in which one click does both. */
    var events = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'click'];
    events.forEach(function (name) {
      document.addEventListener(name, finish, { capture: true, passive: true });
      self.unbinders.push(function () {
        document.removeEventListener(name, finish, { capture: true, passive: true });
      });
    });

    /* Scroll is bound one frame late and compared against the position at bind
       time. Browsers fire a scroll during bfcache and anchor restoration, and a
       stage that skipped itself before the first frame would look like the
       intro had simply never been built. */
    var startY = window.pageYOffset || 0;
    var onScroll = function () {
      if ((window.pageYOffset || 0) !== startY) finish();
    };
    var scrollTimer = window.setTimeout(function () {
      window.addEventListener('scroll', onScroll, { passive: true });
      self.unbinders.push(function () {
        window.removeEventListener('scroll', onScroll, { passive: true });
      });
    }, 0);
    this.timers.push(scrollTimer);

    /* Somebody turning reduced motion on mid-intro is asking for it to stop
       now, not next page load. */
    if (window.matchMedia) {
      var query = window.matchMedia(REDUCED_MOTION_QUERY);
      var onQuery = function () {
        if (query.matches) finish();
      };
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', onQuery);
        this.unbinders.push(function () {
          query.removeEventListener('change', onQuery);
        });
      } else if (typeof query.addListener === 'function') {
        query.addListener(onQuery);
        this.unbinders.push(function () {
          query.removeListener(onQuery);
        });
      }
    }
  };

  /* -------------------------------------------------------------------- push */

  Stage.prototype.push = function () {
    if (this.finished) return;
    var self = this;

    /* Two frames, and both are needed. The first attaches the transition while
       the armed class still holds the start transform, so the start value is
       the one the browser has already computed. The second drops armed, which
       is when the computed transform changes and the transition begins.
       Collapsing this into one frame makes the start value and the transition
       arrive in the same style recalculation, which some engines resolve as "no
       previous value to interpolate from" and paint as an instant jump. */
    this.root.classList.add(CLASS_MOTION);

    window.requestAnimationFrame(function () {
      if (self.finished) return;
      window.requestAnimationFrame(function () {
        if (self.finished) return;
        self.root.classList.remove(CLASS_ARMED);

        var onEnd = function (event) {
          if (event.target !== self.zoom) return;
          if (event.propertyName !== 'transform') return;
          self.finish();
        };
        self.zoom.addEventListener('transitionend', onEnd);
        self.unbinders.push(function () {
          self.zoom.removeEventListener('transitionend', onEnd);
        });

        /* transitionend is not a guarantee. A backgrounded tab, a transform
           that resolves to the value it already had, or an interrupted
           transition all swallow it, and a stage that never finished would keep
           will-change and a transform on the wrapper forever — which is exactly
           the containing-block trap this section exists to avoid. */
        self.timers.push(window.setTimeout(function () {
          self.finish();
        }, self.pushMs + 400));
      });
    });
  };

  /* ------------------------------------------------------------------ finish */

  Stage.prototype.finish = function () {
    if (this.finished) return;
    this.finished = true;

    var i;
    for (i = 0; i < this.timers.length; i++) window.clearTimeout(this.timers[i]);
    this.timers = [];
    for (i = 0; i < this.unbinders.length; i++) this.unbinders[i]();
    this.unbinders = [];

    /* THE POINT OF THE WHOLE PUSH PATH. Removing both classes takes `transform`,
       `will-change` and `transition` out of the computed style entirely,
       because no base rule declares any of them. The wrapper stops being a
       containing block, so a fixed-position dialog opened from the surface
       positions against the viewport again. Setting them to `none` would look
       identical and keep the trap.

       Removing them mid-transition is also the hard cut a skip is supposed to
       be: the transition is cancelled and the element lands on its base state
       in the same frame. */
    this.root.classList.remove(CLASS_MOTION);
    this.root.classList.remove(CLASS_ARMED);

    /* The geometry is now the visitor's business rather than the animation's, so
       the list is handed back. Reached by the skip paths as well as by the
       transition ending, because both of them end the intro — a skipped intro
       must not leave a list that cannot be opened. */
    if (this.menu) this.menu.unlock();

    rememberPlayed(this.sessionKey);

    /* The skip button is going away. If it is what has focus, focus has to go
       somewhere deliberate or it falls back to <body> and the next Tab starts
       again from the top of the document. preventScroll because moving focus
       must not scroll the page out from under somebody who has not asked to
       move. */
    if (this.skipButton) {
      var hadFocus = document.activeElement === this.skipButton;
      this.skipButton.hidden = true;
      if (hadFocus && this.surface) {
        try {
          this.surface.focus({ preventScroll: true });
        } catch (e) {
          this.surface.focus();
        }
      }
    }

    /* Alpha's reveal observer marks anything that has not intersected the
       viewport as offscreen. Content that spent the intro at 22% scale has
       intersected, so this is belt and braces rather than load-bearing — but it
       costs one call and the failure mode it covers is an empty surface. */
    if (this.surface) revealContent(this.surface);
  };

  /* The reference is dropped BEFORE finishing. finish() hands the list back to
     the visitor, and on a teardown there is no visitor left to hand it to — a
     deep link re-opening a category inside a section the theme editor is in the
     middle of replacing would start an animation against markup that is already
     detached. Cancel, never complete, exactly as the panel waiters do. */
  Stage.prototype.destroy = function () {
    var menu = this.menu;
    this.menu = null;
    this.finish();
    if (menu) menu.destroy();
  };

  /* ------------------------------------------------------------- lifecycle */

  function findInstance(root) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root === root) return instances[i];
    }
    return null;
  }

  /* Drop instances whose root the theme editor has detached, releasing their
     document-level listeners and cancelling any half-finished open sequence
     first. Nothing is moved back anywhere — this section owns everything inside
     it, so a detached root takes all of its own markup with it. */
  function sweep() {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (!document.contains(instances[i].root)) {
        instances[i].destroy();
        instances.splice(i, 1);
      }
    }
  }

  function init(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var roots = container.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (findInstance(roots[i])) continue;
      instances.push(new Stage(roots[i]));
    }
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    sweep();
    init(event.target || document);
  });

  document.addEventListener('shopify:section:unload', function () {
    sweep();
  });
})();
